import angular from "angular";

import { Arrays, EnumerationsLogic, InputEnumOption, Objects, Translations } from "@salaxy/core";
import { InputController } from "./InputController";

/**
 * Controller behind form controls that select an occupation type.
 */
export class InputEnumController extends InputController {
  /**
   * Binds to an enumeration defined by the Salaxy API.
   * Set the name of the enumeration.
   */
  public enum: string;

  /** Type of the input element. Options are typeahead, select (default) and radio. Todo: remove 'uib-dropdown' */
  public type: "select" | "radio" | "multi-select" | "dropdown";

  /** Options of the select control as a key-value object. */
  protected _options: any;

  /**
   * Array or comma separated string to filter the option values to just the given ones.
   * Also sets the order to this order, so works for ordering a given set of values.
   * Note that hiddenOptions is applied first, so if you want e.g. "undefined" to appear if selected,
   * you may add it here and it behaves as expected (undefined is still hidden if a value is selevted).
   */
  public filter: string[];

  /** If set to true, does not cache enums. */
  public disableCache;

  /**
   * These values are visible only if they are selected in the data.
   * Default is ["undefined"], set to empty array to show all in every state.
   * I.e. after something else is selected, hidden value cannot be selected back.
   * Use for not-selected values ("Please choose...") when you do not want selection reversed
   * or legacy data that is not selectable, but may still exist on the server.
   */
  public hiddenOptions: string[];

  /**
   * These values are not considered as valid in the UI.
   * Default is ["unknown"] and only used when input is "required" and input type is "select"
   * Use in situations where an unknown/other/none value is set in API but should not be available as an option.
   */
  public invalidEnums: string[];

  /**
   * Label format for the option. The default format is to show only the text of the option.
   * Options are:
   * "text" or empty: only the text of the option is displayed.
   * "text-value": in addition to the text, the value is also displayed in the label.
   * "value": only the value is displayed.
   * "title": Title (description of enumeration) if available. Defaults to text.
   */
  public labelFormat:  "text" | "value" | "text-value" | "title";

  /**
   * If set, last selected option is stored in LocalStorage and set as default/selected value in element.
   * Currently supported only in type 'select' and 'uib-dropbdown'.
   */
  public remember: string;
  private enumCache: InputEnumOption[] = [];

  // TODO: tarpeeton?
  constructor() {
    super();
  }

  /** Sets the default values in init. */
  public $onInit() {
    super.$onInit();
    if (!this.type) {
      this.type = "select";
    }
    if (!this.hiddenOptions) {
      this.hiddenOptions = ["undefined"];
    }
    if (!this.invalidEnums) {
      this.invalidEnums = ["unknown"];
    }
    if (this.remember && window.localStorage.getItem(this.remember+"."+this.name)){
      this.value = this.getStoredValue();
      super.onChange();
    }
  }

  /** Options list */
  public get options(): any[] {
    return this._options;
  }
  public set options(value: any[]) {
    this._options = value;
  }

  /**
   * Gets the label for an enumeration.
   * @param value Type of the enum.
   */
  public getEnumerationLabel(value: string): string {
    const option = this.getEnumerations().find((x) => x.value === value);
    if (option) {
      return this.formatLabel(option);
    }
    return null;
  }

  /** Returns true if the placeholder should be added to the options. */
  public get showPlaceholder(): boolean {
    if (!this.value || this.value === "undefined" || this.value === "unknown") {
      if (!this.getEnumerations().find((x) => x.value === this.value)) {
        return true;
      }
    }
    return false;
  }

  /** Formats the label for display and typeahed searches. */
  public formatLabel(option: InputEnumOption) {
    switch(this.labelFormat) {
      case "value":
        return option.value;
      case "text-value":
        {
          let text = option.text ? Translations.get(option.text) : option.text;
          text = text ? text : option.value;
          return `${text}${option.value ? " (" + option.value +")" : ""}`;
        }
      case "title":
        return Translations.get(option.title) ?? Translations.get(option.text);
      case "text":
      default:
        return Translations.get(option.text);
    }
  }

  /**
   * Returns items for current enumeration.
   */
  protected getEnumerations(): InputEnumOption[] {
    if (this.enumCache.length > 0 && !this.disableCache) {
      return this.enumCache;
    }
    let allValues: InputEnumOption[] = [];
    const selectedValue = this.value;
    if (this.options) {
      if (Array.isArray(this.options)) {
        allValues = this.options.map((item: any) => {
          if (item == null) {
            return { value: null, text: "[null]" };
          }
          if (Objects.has(item, "value") || Objects.has(item, "text")) {
            return {
              value: item.value,
              text: item.text,
              title: (item.title && item.title !== item.value) ? item.title : null,
              ui: item.ui ? item.ui : null
            };
          }
          return { value: item, text: item };
        });
      } else {
        allValues = Object.keys(this.options)
          .map((key) => ({ value: key, text: this.options[key], title: null }));
      }
    } else if (this.enum) {
      const enums = EnumerationsLogic.getEnumMetadata(this.enum);
      if (enums) {
        allValues = enums.values
          .map((e) => ({ value: e.name, text: e.label, title: e.descr && e.descr !== e.name ? e.descr : null }));
      } else {
        allValues = [{
          value: "",
          text: `ERROR: ${this.enum} not found.`,
          title: null,
        }];
      }
    }
    if (this.hiddenOptions) {
      allValues = allValues.filter((option) => {
        const matchingHiddenValue = Arrays.assureArray(this.hiddenOptions).find((hiddenValue) => hiddenValue === option.value);
        // Hidden is shown only if it is currently selected in data.
        return matchingHiddenValue == null || matchingHiddenValue === selectedValue;
      });
    }
    const filterArr = Arrays.assureArray(this.filter);
    if (filterArr.length > 0) {
      allValues = filterArr.map((x) => allValues.find((y) => y.value === x)).filter((x) => x != null);
    }
    this.modifyOptions(allValues, this.enumCache);
    return this.enumCache;
  }

  private modifyOptions(
    source: ({
      /** Enum value. */
      value: any,
      /** Label for the value. */
      text: string,
      /** Description for the value. */
      title?: string,
    })[],
    target: ({
      /** Enum value. */
      value: any,
      /** Label for the value. */
      text: string,
      /** Description for the value. */
      title?: string,
    })[]) {
    // check if source values differ from target
    const ok = angular.equals(source, target);

    if (ok) {
      return;
    }
    // remove values
    target.splice(0, target.length);

    // add values
    target.push(...source);
  }

  /** Store selection to LocalStorage */
  public setStoredValue(inputName: any, value: any){
    if(this.remember && window.localStorage){
      const storageKey = this.remember +"." + inputName;
      window.localStorage.setItem(storageKey, value);
    }
  }

  /** Get stored value from LocalStorage*/
  public getStoredValue(){
    if(this.remember && window.localStorage){
      const storageKey = this.remember +"." + this.name;
      return window.localStorage.getItem(storageKey);
    }
    return null;
  }
}